import React, { useEffect, useRef, useState } from 'react';
import { View, FlatList, Animated, Easing } from 'react-native';
import { FlatListPerformanceView } from '../../libraries/ReactNativePerformanceShopify';
import { useDispatch } from 'react-redux';
import { fetchList } from '../../actions/ActionTypes';
import List from '../List/List';
import { isBlank, isPresent } from '../../utils/BooleanUtility';
import { SCREEN_CONSTANTS } from '../../config/ScreenConstants';
import { styles } from './styles';
import ErrorBoundaryComponent from '../../components/shared/ErrorBoundaryComponent';
import TabGroup from './TabGroup';
import ListPlaceHolder from '../../components/shared/GridScreenPlaceHolder';
import { getItemName } from '../../utils/PerfUtility';
import { SEP } from '../../config/Constants';
import ListsFilter from '../../components/layout/ListsFilter';
import { filteredRequiredAttribute } from '../../utils/UriUtility';
import FilterResultsEmptyState from '../../components/sort-filter-options/FilterResultsEmptyState';
import FilterUtils, { canOpenSortingModal } from '../../components/sort-filter-options/FilterUtils';
import { useNavigation } from '@react-navigation/native';
import ListWithFilterComponent from '../List/ListWithFilterComponent';
import Utility from '../../utils/Utility';

const RenderShimmer = ({ display }) => {
  const columnCount = display === 'section' ? 4 : 1;
  return (
    <View style={styles.shimmer}>
      <ListPlaceHolder columnCount={columnCount} />
    </View>
  );
};

const MultiLevelTabs = (props) => {
  const {
    item = [],
    previousScreen = '',
    listData = {},
    parentListContent = '',
    parentListIndex = '',
    listIndex = '',
    parentListDisplay = '',
    tabListWidth,
    parentListsData = [],
  } = props;

  const dispatch = useDispatch();
  const navigation = useNavigation();
  const [selectedList, setSelectedList] = useState(item[1]);
  const [tabs, setTabsData] = useState(item);
  const [defaultList, setDefaultList] = useState(item[1]);
  const [isListLoading, setListLoading] = useState(false);
  const [verticalOffsetAnimator, setVerticalOffsetAnimator] = useState(new Animated.Value(0));
  const [requiredUserAttributes, setRequiredUserAttributes] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});
  const [filters, setFilters] = useState([]);
  const [quickFilters, setQuickFilters] = useState([]);
  const [showProductShimmer, setShowProductShimmer] = useState(false);
  const [hideOosProduct, setHideOosProduct] = useState(false);
  const [filteredSlug, setFilteredSlug] = useState('');
  const fnAutoListPaginationRef = useRef(null);
  let pageNo = 0;

  let scrollY = useRef(0).current;
  const flatListRef = useRef(null);
  const quickFiltersRef = useRef(null);
  const prevAppliedFiltersFromModal = useRef([]);
  const selectedQuickFilters = useRef([]);

  const animatedStyle = {
    transform: [
      {
        translateY: verticalOffsetAnimator.interpolate({
          inputRange: [0, 1],
          outputRange: [0, -350],
        }),
      },
    ],
  };

  const handleScroll = (event) => {
    const currentScrollY = event?.nativeEvent?.contentOffset?.y;
    if (currentScrollY <= scrollY || currentScrollY <= 120 || scrollY === 0) {
      Animated.timing(verticalOffsetAnimator, {
        toValue: 0,
        duration: 80,
        easing: Easing.linear,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(verticalOffsetAnimator, {
        toValue: scrollY <= 0 || currentScrollY <= 0 ? 0 : 1,
        duration: 80,
        easing: Easing.linear,
        useNativeDriver: true,
      }).start();
    }
    scrollY = currentScrollY;
  };

  useEffect(() => {
    handleScroll();
  }, []);

  useEffect(() => {
    updateStateFromProps();
  }, [selectedList]);

  useEffect(() => {
    if (isPresent(selectedList)) {
      const { filters, quickFilters } = FilterUtils.getFiltersFromList(selectedList);
      setFilters(filters);
      setQuickFilters(quickFilters);
    } else if (isPresent(defaultList)) {
      const { filters, quickFilters } = FilterUtils.getFiltersFromList(defaultList);
      setFilters(filters);
      setQuickFilters(quickFilters);
    }
  }, [selectedList, defaultList]);

  const updateStateFromProps = () => {
    const updatedTabs = tabs;
    updatedTabs[1] = selectedList;
    setTabsData([...updatedTabs]);
  };

  if (isBlank(tabs)) return null;

  const setSelectedListData = (selectedData = {}) => {
    const { list: objects = [], slug } = Utility.createFilterObjectIfExists(
      selectedData?.objects || [],
      true,
      false,
      true,
    );
    setFilteredSlug(slug);
    if (isPresent(objects)) {
      const updatedSelectedData = { ...selectedData, objects };
      setSelectedList(updatedSelectedData);
    }
    scrollToTop();
  };

  const setListLoadingState = (loadingState) => {
    setListLoading(loadingState);
  };

  const setListRef = (ref) => {
    flatListRef.current = ref;
  };

  const scrollToTop = () => {
    flatListRef.current?.scrollToOffset({ animated: true, offset: 0 });
  };

  const fetchFilteredList = (slug = '', hideOosProduct = false) => {
    if (isBlank(slug)) return;

    let url = slug;
    const isPersonalizedUrl = url.includes('personalized') && isBlank(requiredUserAttributes)

    if (isPresent(requiredUserAttributes) || isPersonalizedUrl) {
      url = filteredRequiredAttribute(url, requiredUserAttributes, isPersonalizedUrl);
    }

    setShowProductShimmer(true);
    setListLoading(true);

    dispatch(
      fetchList({ slug: url, hideOosProduct }, pageNo, (success, data) => {
        setShowProductShimmer(false);
        setListLoading(false);

        if (!success) return;

        if (selectedList.content === 'mixed') {
          const listData = FilterUtils.appendFilteredListToMasterListObjects(slug, data, selectedList);

          setSelectedList({ ...listData });
          if (selectedList.paginate_last_item && fnAutoListPaginationRef.current) {
            fnAutoListPaginationRef.current(url, -1, true);
          }
          return;
        }
        setSelectedList(data);
      }),
    );
  };

  const onFiltersPress = (item, added, index, resetQuickFilter, hideOosProductParam = hideOosProduct) => {
    const currentList = selectedList || defaultList;
    const { list: productList } = FilterUtils.getList(currentList);

    const shouldOpenSortingModal = canOpenSortingModal(item, selectedFilters);

    if (shouldOpenSortingModal) {
      navigation.navigate('SortingFilteringModal', {
        selectedFilters,
        modalFilters: filters,
        onFilterApplyPress: onFiltersPress,
        listName: currentList?.name || '',
        listId: currentList?.id || '',
        listSlug: currentList?.slug || '',
        listData: currentList,
        screenName: previousScreen,
        entityName: currentList?.name || '',
        showFoxyMatchModal: item.criteria_name === 'quick_filter_foxy_match',
        hideOosProduct: hideOosProductParam,
        onPressHideOosCheckBox: (isHideOos) => setHideOosProduct(isHideOos),
        resetQuickFilter,
      });
      return;
    }

    const {
      slug,
      pageNo,
      allowNextPageLoad,
      filtersEnabled: newFiltersEnabled,
      groupedSelectedFilters,
      selectedQuickFilters: newSelectedQuickFilters,
      prevAppliedFiltersFromModal: newPrevAppliedFilters,
    } = FilterUtils.onFilterApplied(
      item,
      added,
      index,
      productList,
      selectedFilters,
      quickFiltersRef.current,
      filteredSlug || '',
      prevAppliedFiltersFromModal.current,
      selectedQuickFilters.current,
      previousScreen,
      productList?.name || '',
      hideOosProductParam,
    );

    selectedQuickFilters.current = newSelectedQuickFilters;
    prevAppliedFiltersFromModal.current = newPrevAppliedFilters;

    setSelectedFilters(groupedSelectedFilters);
    setFilteredSlug(slug);
    setSelectedList({ ...selectedList, objects: Utility.addShimmerInListObject(selectedList)});

    setShowProductShimmer(true);

    if (slug) {
      fetchFilteredList(slug, hideOosProductParam, groupedSelectedFilters);
    }
  };

  const setListPaginatedCallApiRef = (fnAutoListPagination) => {
    fnAutoListPaginationRef.current = fnAutoListPagination;
  };

  const removeSelectedFilters = () => {
    setSelectedFilters({});
    quickFiltersRef.current?.resetQuickFilters();
    selectedQuickFilters.current = [];
    pageNo = 0;
  }

  const resetFilters = () => {
    removeSelectedFilters();
    setShowProductShimmer(true);
    const currentList = selectedList || defaultList;
    const { list: productList } = FilterUtils.getList(currentList);
    setFilteredSlug(productList.slug.split('&')[0]);
    setTimeout(() => {
      if (productList?.slug) {
        fetchFilteredList(productList.slug.split('&')[0], false, {}); // Remove existing filter params
      }
    }, 0);
  };

  const renderItem = ({ item, index }) => {
    if (isBlank(item) && isBlank(item?.objects)) return null;

    if (item?.type === 'list') {
      const { required_user_attributes = {} } = item;
      setRequiredUserAttributes(required_user_attributes);

      if (isListLoading && previousScreen !== SCREEN_CONSTANTS.MORE_PAGE) {
        return <RenderShimmer display={defaultList.display} />;
      }

      // added this to fix the vertical scroll jerk issue when multi level tablist is curated at the bottom of the list.
      let listContainerStyle =
        parentListIndex >= 1 && parentListContent === 'mixed' && defaultList.display === 'grid'
          ? styles.gridListContainer
          : styles.listContainer;
      if (listData.display_order && listData.display_order < parentListsData.length - 1) {
        listContainerStyle = [listContainerStyle, { paddingBottom: 0 }];
      }

      return (
        <View style={listContainerStyle}>
          <>
            <List
              itemData={selectedList || defaultList}
              setListRef={setListRef}
              previousScreen={previousScreen}
              key={(selectedList || defaultList)?.id}
              listData={listData}
              hideOosProduct={hideOosProduct}
              selectedFilters={selectedFilters}
              onFiltersPress={onFiltersPress}
              loadMore={showProductShimmer}
              setListPaginatedCallApiRef={setListPaginatedCallApiRef}
              filteredSlug={filteredSlug}
              onPressResetButton={resetFilters}
            />
          </>
        </View>
      );
    }

    return (
      <ErrorBoundaryComponent itemData={item} listData={listData} screenName={previousScreen}>
        <TabGroup
          itemData={item?.objects}
          display={item?.display}
          setSelectedTabList={setSelectedListData}
          index={index}
          defaultList={defaultList}
          listData={listData}
          previousScreen={previousScreen}
          setListLoadingState={setListLoadingState}
          animatedStyle={animatedStyle}
          parentListContent={parentListContent}
          listIndex={listIndex}
          parentListDisplay={parentListDisplay}
          requiredUserAttributes={requiredUserAttributes}
          tabListWidth={tabListWidth}
          removeSelectedFilters={removeSelectedFilters}
        />
      </ErrorBoundaryComponent>
    );
  };

  if (isBlank(tabs)) return null;
  const keyExtractor = (item, index) => `${index}_${item?.type}`;

  return (
    <View style={styles.rootContainer}>
      <FlatList
        data={tabs}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        showsHorizontalScrollIndicator={false}
        stickyHeaderIndices={[0]}
        ref={setListRef}
        extraData={{ isListLoading, selectedFilters, showProductShimmer }}
        onScroll={handleScroll}
        bounces={false}
      />
    </View>
  );
};

export default MultiLevelTabs;